import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useGuestSessionIdentity } from '@shared/hooks/useGuestSessionIdentity';
import { useSeedFieldOnce } from '@shared/hooks/useSeedFieldOnce';

import type { RegistryOrderFragment } from '@graphql/fragments/registry.graphql.generated';
import { useTranslation } from '@shared/core';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { Box } from '@withjoy/joykit';
import type { useRoutePaths } from '@apps/registry/guest/GuestRegistry.routes';
import { CartReviewStep, DetailsStep, ShippingStep, PaymentStep } from './steps';
import { ContentContainer, ContentFlex, ContentWrapper, ResizedLogo, StyledScrollableFlex, styles } from './ShoppingCartInnerV2.styles';
import { useShoppingCart } from '../state';
import type { InCartProduct, ProductListV2 } from '../ShoppingCart.types';
import { CartCheckoutSteps, ShippingAddressOptions } from '../ShoppingCart.types';
import { CartSections } from '../ShoppingCart.constants';
import { Breadcrumbs, CartFooterAtoms, CartFooterSubmitButton, SummaryItemListContainer } from '../components';
import { noop } from 'lodash-es';
import type { DetailsFormFields } from './steps/DetailsStep/types';
import type { ECard } from '@graphql/generated-base';
import type { GiftWrapOfferState, GiftWrapOption } from '@apps/registry/guest/components/CheckoutDialog/components/GiftWrapOffer/GiftWrapOffer';
import { useDetailsFormFields } from './steps/DetailsStep/hooks';
import MessageStep from './steps/MessageStep';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { useShippingStepController } from './steps/ShippingStep/ShippingStep.controller';

type ShoppingCartInnerProps = {
  eventId: string;
  helpDialogOpen: boolean;
  isAddExternalOrderTrackingDialogOpen: boolean;
  productList: ProductListV2;
  reservedOrderList: RegistryOrderFragment[];
  purchasedOrderList: RegistryOrderFragment[];
  orderCount: number;
  cartOrderCount: number;
  cartTotal: string;
  inCartProductList: Array<InCartProduct>;
  inCartProductCount: number;
  inCartProductTotal: number;
  orderForTrackingRequest: Maybe<RegistryOrderFragment>;
  paths: ReturnType<typeof useRoutePaths>;
  hideCartHeader?: boolean;
  showHelpDialog: () => void;
  handleDialogClose: () => void;
  closeAddExternalOrderTrackingDialog: () => void;
  registryCurrencyCode?: string;
  eventHandle: string;
};

const getIsDisabledNextStep = (
  inCartProductTotal: number,
  inCartProductCount: number,
  isFormValid: boolean,
  currentCheckoutStep: CartCheckoutSteps,
  addressOption: ShippingAddressOptions,
  isCustomShippingAddressValid: boolean,
  isCoupleAddressValid: boolean,
) => {
  switch (currentCheckoutStep) {
    case CartCheckoutSteps.Payment:
      return inCartProductTotal === 0 || inCartProductCount === 0;
    case CartCheckoutSteps.Details:
      // Deliberately not `dirty`: details can now arrive prefilled from the guest's
      // session, and a guest who never had to type is not stuck behind a dead button.
      // `validateOnMount` makes `isFormValid` trustworthy for a blank form on its own.
      return inCartProductTotal === 0 || inCartProductCount === 0 || !isFormValid;
    case CartCheckoutSteps.Shipping:
      return addressOption === ShippingAddressOptions.ShipToMe ? !isCustomShippingAddressValid : !isCoupleAddressValid;
    default:
      return false;
  }
};

export const ShoppingCartInnerV2: React.FC<ShoppingCartInnerProps> = ({
  eventId,
  eventHandle,
  helpDialogOpen,
  isAddExternalOrderTrackingDialogOpen,
  productList,
  inCartProductList,
  inCartProductCount,
  inCartProductTotal,
  reservedOrderList,
  purchasedOrderList,
  cartOrderCount,
  orderForTrackingRequest,
  paths,
  registryCurrencyCode,
  hideCartHeader,
  showHelpDialog,
  handleDialogClose,
  closeAddExternalOrderTrackingDialog,
}) => {
  const isMobileOrTablet = useIsMobileOrTablet();
  const { formatCurrency } = useCurrencyFormatter();

  const {
    state: {
      currentCheckoutStep: currentStep,
      details,
      currentSection,
      shippingFeeInMajorUnits,
      additionalCostInMajorUnits,
      taxesTotalInMajorUnits,
      checkoutStepsFlow,
      expressCheckoutEnabled,
    },
    mutators: { updateCurrentCheckoutStep, updateDetails, updatePurchaseInfoGiftWrap },
  } = useShoppingCart();

  const { t } = useTranslation('sharedRegistry');
  const { cart, shipping, details: detailsText, message, payment } = t('shoppingCart', 'steps');
  const stepNamesMap = useMemo(
    () =>
      new Map([
        [CartCheckoutSteps.Cart, cart],
        [CartCheckoutSteps.Shipping, shipping],
        [CartCheckoutSteps.Details, detailsText],
        [CartCheckoutSteps.Message, message],
        [CartCheckoutSteps.Payment, payment],
      ]),
    [cart, shipping, detailsText, message, payment],
  );

  const subtotal = registryCurrencyCode
    ? formatCurrency({
        priceFloatingPointDecimalString: `${inCartProductTotal}`,
        priceCurrencyCode: DEFAULT_CURRENCY,
        formatForm: 'short',
      })
    : inCartProductTotal.toString();

  const totalInMajorUnits = inCartProductTotal + shippingFeeInMajorUnits + additionalCostInMajorUnits + (taxesTotalInMajorUnits ?? 0);
  const total = registryCurrencyCode
    ? formatCurrency({
        priceFloatingPointDecimalString: `${totalInMajorUnits}`,
        priceCurrencyCode: DEFAULT_CURRENCY,
        formatForm: 'short',
      })
    : totalInMajorUnits.toString();

  const [firstInCartProduct] = inCartProductList;

  const handleStepClick = (step: CartCheckoutSteps) => {
    if (currentStep !== step) {
      updateCurrentCheckoutStep(step);
    }
  };

  // wrapOption drives the live eCard price display in the summary panel as the user
  // toggles between "Personalized eCard" and "Basic Gift Note". Defaults to 'eCard'
  // to match GiftWrapOffer's own default so the price shows from the Shipping step onward.
  const [wrapOption, setWrapOption] = useState<GiftWrapOption>('eCard');

  // eCardRef holds the prepared payload from MessageStep. We use a ref instead of state
  // because setECard and saveData (formik.submitForm) are called back-to-back in the same
  // synchronous block — React state updates would not commit in time for handleFormSubmit
  // to read the new value.
  const eCardRef = useRef<ECard | undefined>(details.eCard ?? undefined);

  const handleFormSubmit = (formValues: DetailsFormFields) => {
    updateDetails({ ...formValues, eCard: eCardRef.current });
  };

  const { saveDetails, formik, ...formFields } = useDetailsFormFields(details, handleFormSubmit);

  // Prefill the details step for a guest the event session already knows, so they don't
  // re-type a name and email we have. Skipped once the cart carries details of its own.
  const { fullName: sessionName, email: sessionEmail } = useGuestSessionIdentity({
    eventHandle,
    skip: Boolean(details.name && details.email),
  });

  // Seeded here rather than through `initialValues`: this form runs with
  // `enableReinitialize`, so folding an async value into initialValues would reset the
  // whole form — and wipe whatever the guest had typed — the moment the read lands.
  // Each field is filled once, and only if we get there before the guest does.
  const { setFieldValue: setDetailsFieldValue, touched: detailsTouched, values: detailsValues } = formik;
  const seedDetailsName = useCallback((value: string) => setDetailsFieldValue('name', value), [setDetailsFieldValue]);
  const seedDetailsEmail = useCallback((value: string) => setDetailsFieldValue('email', value), [setDetailsFieldValue]);
  useSeedFieldOnce({ seedValue: sessionName, currentValue: detailsValues.name, isTouched: detailsTouched.name, onSeed: seedDetailsName });
  useSeedFieldOnce({ seedValue: sessionEmail, currentValue: detailsValues.email, isTouched: detailsTouched.email, onSeed: seedDetailsEmail });

  const handleGiftWrapChange = useCallback(
    (giftWrap: GiftWrapOfferState) => {
      setWrapOption(giftWrap.wrapOption);
      updatePurchaseInfoGiftWrap(giftWrap);
    },
    [updatePurchaseInfoGiftWrap],
  );

  const {
    isFormOpen,
    setIsFormOpen,
    formFields: shippingFormFields,
    formik: shippingFormik,
    coupleAddress,
    supportsShipToMe,
    isLowInventory,
    shippingFee,
    shippingOptionSelected,
    setShippingOptionSelected,
    setSuggestedAddress,
    setAddressUsed,
    updateShippingOption,
    shippingAddressFormValues,
    customShippingAddress,
    saveAddress,
    isCustomShippingAddressValid,
    isCoupleAddressValid,
  } = useShippingStepController();
  const hasMultipleGifts = inCartProductList.length > 1;

  const isDisabledNextStep = useMemo(
    () =>
      getIsDisabledNextStep(
        inCartProductTotal,
        inCartProductCount,
        formFields.isFormValid,
        currentStep,
        shippingOptionSelected,
        isCustomShippingAddressValid,
        isCoupleAddressValid,
      ),
    [inCartProductTotal, inCartProductCount, formFields.isFormValid, currentStep, shippingOptionSelected, isCustomShippingAddressValid, isCoupleAddressValid],
  );
  const hasECard = wrapOption === 'eCard' && currentStep === CartCheckoutSteps.Message;

  const onContinueMessageStep = () => {
    updateCurrentCheckoutStep(CartCheckoutSteps.Payment);
  };

  return (
    <>
      <StyledScrollableFlex>
        {currentStep === CartCheckoutSteps.Cart ? (
          <CartReviewStep
            eventId={eventId}
            cartOrderCount={cartOrderCount}
            inCartProductCount={inCartProductCount}
            inCartProductTotal={inCartProductTotal}
            inCartProductList={inCartProductList}
            helpDialogOpen={helpDialogOpen}
            showHelpDialog={showHelpDialog}
            registryCurrencyCode={registryCurrencyCode}
            isAddExternalOrderTrackingDialogOpen={isAddExternalOrderTrackingDialogOpen}
            productList={productList}
            purchasedOrderList={purchasedOrderList}
            reservedOrderList={reservedOrderList}
            paths={paths}
            hideHeader={hideCartHeader}
            closeAddExternalOrderTrackingDialog={closeAddExternalOrderTrackingDialog}
            orderForTrackingRequest={orderForTrackingRequest}
            handleDialogClose={handleDialogClose}
            shippingAddressSectionProps={{
              isFormOpen,
              setIsFormOpen,
              formFields: shippingFormFields,
              formik: shippingFormik,
              coupleAddress,
              supportsShipToMe,
              isLowInventory,
              hasMultipleGifts,
              shippingFee,
              shippingOptionSelected,
              setShippingOptionSelected,
              setSuggestedAddress,
              setAddressUsed,
              updateShippingOption,
              shippingAddressFormValues,
              customShippingAddress,
              saveAddress,
            }}
          />
        ) : (
          <ContentFlex __css={styles.contentFlexStyles}>
            <ContentContainer __css={styles.contentContainerStyles}>
              <ContentWrapper __css={styles.contentWrapperStyles}>
                <Box>
                  <ResizedLogo />
                </Box>
                <Box
                  marginBottom={7}
                  marginTop={5}
                >
                  <Breadcrumbs
                    atoms={checkoutStepsFlow.map(step => ({
                      label: stepNamesMap.get(step)?.() ?? '',
                      isActive: currentStep === step,
                      onClick: () => handleStepClick(step),
                    }))}
                  />
                </Box>
                {currentStep === CartCheckoutSteps.Shipping && (
                  <ShippingStep
                    isFormOpen={isFormOpen}
                    setIsFormOpen={setIsFormOpen}
                    formFields={shippingFormFields}
                    formik={shippingFormik}
                    coupleAddress={coupleAddress}
                    supportsShipToMe={supportsShipToMe}
                    isLowInventory={isLowInventory}
                    hasMultipleGifts={hasMultipleGifts}
                    shippingFee={shippingFee}
                    shippingOptionSelected={shippingOptionSelected}
                    setShippingOptionSelected={setShippingOptionSelected}
                    setSuggestedAddress={setSuggestedAddress}
                    setAddressUsed={setAddressUsed}
                    updateShippingOption={updateShippingOption}
                    shippingAddressFormValues={shippingAddressFormValues}
                    customShippingAddress={customShippingAddress}
                    saveAddress={saveAddress}
                    disableNextStep={isDisabledNextStep}
                  />
                )}
                {currentStep === CartCheckoutSteps.Details && (
                  <DetailsStep
                    saveData={saveDetails}
                    formFields={formFields}
                    disableNextStep={isDisabledNextStep}
                  />
                )}
                {currentStep === CartCheckoutSteps.Message &&
                  (firstInCartProduct ? (
                    <MessageStep
                      eventId={eventId}
                      senderName={formik.values.name}
                      saveData={saveDetails}
                      setMessage={message => formik.setFieldValue('message', message)}
                      setECard={eCard => {
                        eCardRef.current = eCard;
                      }}
                      onGiftWrapChange={handleGiftWrapChange}
                      disableNextStep={isDisabledNextStep}
                      onContinue={onContinueMessageStep}
                      product={{
                        title: firstInCartProduct.product.title,
                        image: firstInCartProduct.product.image,
                        store: firstInCartProduct.product.storeName || firstInCartProduct.product.brand || undefined,
                        extraProductsCount: inCartProductList.length - 1,
                      }}
                      currencyCode={firstInCartProduct.product.currencyCode}
                    />
                  ) : (
                    <CartFooterSubmitButton
                      saveData={saveDetails}
                      disabled={isDisabledNextStep}
                    />
                  ))}
                {currentStep === CartCheckoutSteps.Payment && (
                  <PaymentStep
                    eventId={eventId}
                    eventHandle={eventHandle}
                  />
                )}
              </ContentWrapper>
            </ContentContainer>
            {!isMobileOrTablet && [CartCheckoutSteps.Shipping, CartCheckoutSteps.Details, CartCheckoutSteps.Message].includes(currentStep) && (
              <SummaryItemListContainer
                inCartProductList={inCartProductList}
                inCartProductTotal={inCartProductTotal}
                hasECard={hasECard}
                registryCurrencyCode={registryCurrencyCode}
                setIsOpen={noop}
              />
            )}
          </ContentFlex>
        )}
      </StyledScrollableFlex>
      {(currentSection === CartSections.CART && currentStep === CartCheckoutSteps.Cart && (
        <CartFooterAtoms
          subtotal={subtotal}
          inCartProductList={inCartProductList}
          inCartProductTotal={inCartProductTotal}
          registryCurrencyCode={registryCurrencyCode}
          expressCheckoutEnabled={expressCheckoutEnabled}
        />
      )) ||
        (isMobileOrTablet &&
          currentSection !== CartSections.HISTORY &&
          currentSection !== CartSections.ADDRESS &&
          currentStep !== CartCheckoutSteps.Payment &&
          currentStep !== CartCheckoutSteps.Message && (
            <CartFooterAtoms
              total={total}
              inCartProductList={inCartProductList}
              inCartProductTotal={inCartProductTotal}
              registryCurrencyCode={registryCurrencyCode}
              saveData={saveDetails}
              hasECard={hasECard}
              disableNextStep={isDisabledNextStep}
              expressCheckoutEnabled={expressCheckoutEnabled}
            />
          ))}
    </>
  );
};
